import {cloneDeep, pick} from 'lodash';

import {get, post} from 'common/services/lucid-service';
import {makeFormData} from 'common/utils/form-util';

// this line breaks tests but not the app...
// import {getChildFormByPrefix} from 'common/components/form/utils';
// so just re-declaring
const getChildFormByPrefix = (form, prefix) =>
  form.formset.forms.find((child) => child.prefix === prefix);

// Action Types
export const UPDATE_FORM_SET = 'UPDATE_FORM_SET';
export const UPDATE_FORM_VALUE = 'UPDATE_FORM_VALUE';
export const ADD_FORMSET_CHILD_FORM = 'ADD_FORMSET_CHILD_FORM';
export const SET_FORM_DATA = 'SET_FORM_DATA';
export const SET_FORM_STEP = 'SET_FORM_STEP';
export const CLEAR_FORM = 'CLEAR_FORM';
export const FETCH_FORM_DATA_START = 'FETCH_FORM_DATA_START';
export const FETCH_FORM_DATA_SUCCESS = 'FETCH_FORM_DATA_SUCCESS';
export const FETCH_FORM_DATA_FAILURE = 'FETCH_FORM_DATA_FAILURE';
export const SUBMIT_FORM_START = 'SUBMIT_FORM_START';
export const SUBMIT_FORM_SUCCESS = 'SUBMIT_FORM_SUCCESS';
export const SUBMIT_FORM_FAILURE = 'SUBMIT_FORM_FAILURE';

// Action Creators
export const setFormData = (form) => ({
  type: SET_FORM_DATA,
  payload: {
    form,
  },
});

export const updateFormValue = (name, value, prefix) => ({
  type: UPDATE_FORM_VALUE,
  payload: {
    name,
    value,
    prefix,
  },
});

export const updateFormSet = (childForm, meta) => ({
  type: UPDATE_FORM_SET,
  payload: {
    childForm,
    meta,
  },
});

export const addFormSetChildForm = () => ({
  type: ADD_FORMSET_CHILD_FORM,
  payload: {},
});

export const setFormStep = (step) => ({
  type: SET_FORM_STEP,
  payload: {
    step,
  },
});

export const clearForm = () => ({
  type: CLEAR_FORM,
  payload: {},
});

export const fetchFormDataStart = () => ({
  type: FETCH_FORM_DATA_START,
  payload: {},
});

export const fetchFormDataSuccess = (form) => ({
  type: FETCH_FORM_DATA_SUCCESS,
  payload: {
    form,
  },
});

export const fetchFormDataFailure = (errorMessage) => ({
  type: FETCH_FORM_DATA_FAILURE,
  payload: {
    errorMessage,
  },
});

export const submitFormStart = () => ({
  type: SUBMIT_FORM_START,
  payload: {},
});

export const submitFormSuccess = (form) => ({
  type: SUBMIT_FORM_SUCCESS,
  payload: {
    form,
  },
});

export const submitFormFailure = (errorMessage) => ({
  type: SUBMIT_FORM_FAILURE,
  payload: {
    errorMessage,
  },
});

// Thunk Actions

// GET request to fetch initial form
export const fetchFormData = (url) => async (dispatch) => {
  dispatch(fetchFormDataStart());

  const response = await get(url, false, false, {
    'X-Form-Json': '1',
  });

  if (typeof response === 'string') {
    dispatch(fetchFormDataFailure(response));
    return;
  }

  dispatch(fetchFormDataSuccess(response));
};

// POST request to handle form change
export const postFormData =
  ({
    changedFieldName,
    changedFieldValue,
    childFormPrefix,
    form,
    onSuccess,
    save,
    url,
  }) =>
  async (dispatch) => {
    const data = cloneDeep(form.data);

    Object.assign(data, {
      csrfmiddlewaretoken: window.settings.csrf,
    });

    if (save) {
      data.save = 1;
    }

    // normalize formset form data into POST data with prefixes
    if (form.formset) {
      form.formset.forms.forEach((childForm) => {
        for (const [childName, childValue] of Object.entries(childForm.data)) {
          const field = childForm.fields[childName];
          const name = `${childForm.prefix}-${childName}`;
          if (field.custom_data_structure) {
            data[name] = JSON.stringify(childValue);
          } else {
            data[name] = childValue;
          }
        }
      });

      const metaUpdates = pick(form.formset.meta, [
        'INITIAL_FORMS',
        'MAX_NUM_FORMS',
        'MIN_NUM_FORMS',
        'TOTAL_FORMS',
      ]);
      for (const [name, value] of Object.entries(metaUpdates)) {
        data[`${form.formset.meta.prefix}-${name}`] = value;
      }
    }

    if (changedFieldName) {
      const name = childFormPrefix
        ? `${childFormPrefix}-${changedFieldName}`
        : changedFieldName;

      data.changed_field = name;

      const field = childFormPrefix
        ? getChildFormByPrefix(form, childFormPrefix).fields[changedFieldName]
        : form.fields[changedFieldName];

      const changedFieldOldValue = childFormPrefix
        ? getChildFormByPrefix(form, childFormPrefix).data[changedFieldName]
        : form.data[changedFieldName];

      if (field.custom_data_structure) {
        data[name] = JSON.stringify(changedFieldValue);
        data.changed_field_old_value = JSON.stringify(changedFieldOldValue);
      } else {
        data[name] = changedFieldValue;
        data.changed_field_old_value = changedFieldOldValue;
      }
    }

    const isForFormSet = form.formset && childFormPrefix && !save;
    const formData = makeFormData(Object.entries(data));

    if (save) {
      dispatch(submitFormStart());
      try {
        const response = await post(url, formData, false, {
          'X-Form-Json': '1',
        });

        dispatch(submitFormSuccess(response));

        const hasErrors = Object.entries(response.errors || {}).length > 0;
        if (onSuccess && !hasErrors) {
          onSuccess();
        }
      } catch (error) {
        dispatch(submitFormFailure(error));
      }
    } else {
      dispatch(fetchFormDataStart());
      try {
        const formJson = await post(url, formData, false, {
          'X-Form-Json': '1',
        });
        if (isForFormSet) {
          const {meta} = formJson.formset;
          const childForm = getChildFormByPrefix(formJson, childFormPrefix);
          dispatch(updateFormSet(childForm, meta));
        } else {
          dispatch(fetchFormDataSuccess(formJson));
        }
      } catch (error) {
        dispatch(fetchFormDataFailure(error));
      }
    }
  };
